import React, { useEffect, useMemo, useState } from 'react';
import cn from 'classnames';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import styles from './index.module.scss';

import Options from './Options';
import { createValueMap, getOpenState } from './utils';

/**
 * @param {Object} props
 * @param {boolean} props.isMulti
 * @param {import('./types').Option[]} props.options
 * @param {Record<string, boolean>} props.value
 * @param {(value: Record<string, boolean>, id: string) => void} props.onChange
 * @param {boolean} props.disabled
 * @param {string} props.className
 */
export default function NestedCheckboxes({
  isMulti = true,
  options = [],
  value = {},
  onChange = () => {},
  disabled = false,
  className = ''
}) {
  const [openMap, setOpenMap] = useState(() => getOpenState(options));

  useEffect(() => {
    setOpenMap(getOpenState(options));
  }, [options]);

  const defaultValues = useMemo(() => createValueMap(options), [options]);

  const hasSubOptions = useMemo(() => {
    return options.some(option => !_isEmpty(_get(option, 'options', [])));
  }, [options]);

  const onToggle = id => {
    if (!id || disabled) return;
    setOpenMap(prev => ({ ...prev, [id]: !prev[id] }));
  };

  /**
   * @param {(id: string, checked: boolean) => void} props.onChange
   * */
  const onValueChange = (id = '', checked = false) => {
    if (!id || disabled) return;
    const newValue = isMulti
      ? { ...value, [id]: checked }
      : { ...defaultValues, [id]: checked };
    onChange(newValue, id);
  };

  return (
    <Options
      list={options}
      value={value}
      onChange={onValueChange}
      isDisabled={disabled}
      openMap={openMap}
      onToggle={onToggle}
      className={cn(
        styles.container,
        { [styles.offset]: hasSubOptions },
        className
      )}
    />
  );
}
